import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  mfaEnabled: boolean;
  aal: "aal1" | "aal2";
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  enrollMFA: () => Promise<{ data: any; error: any }>;
  verifyMFA: (factorId: string, challengeId: string, code: string) => Promise<{ error: any }>;
  unenrollMFA: (factorId: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [aal, setAal] = useState<"aal1" | "aal2">("aal1");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const activeSessionRef = React.useRef(false);

  useEffect(() => {
    activeSessionRef.current = !!user;
  }, [user]);

  useEffect(() => {
    // IMPORTANT: onAuthStateChange callbacks must NOT await Supabase API calls.
    // Doing so creates a deadlock where the auth state change can't complete
    // because it's waiting on another auth call that itself depends on auth state.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY") {
          navigate("/reset-password");
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Do not block UI on background token refreshes or cross-tab syncs
          const isNewLogin = !activeSessionRef.current;
          const shouldBlock = isNewLogin && (event === "INITIAL_SESSION" || event === "SIGNED_IN");
          if (shouldBlock) {
            setIsLoading(true);
          }
          Promise.all([
            checkMFAStatus(),
            checkAdminStatus(session.user.id)
          ]).finally(() => {
            if (shouldBlock) setIsLoading(false);
          });
        } else {
          setIsAdmin(false);
          setMfaEnabled(false);
          setAal("aal1");
        }
      }
    );

    // Initial session load — here we CAN await since this is not inside the listener
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session) {
        await checkMFAStatus();
        await checkAdminStatus(session.user.id);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Idle session timeout (HIGH-11)
  useEffect(() => {
    if (!session) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      // 30 minutes = 30 * 60 * 1000 = 1800000ms
      timeoutId = setTimeout(() => {
        toast.info("Session expired due to inactivity");
        signOut();
      }, 1800000);
    };

    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach(event => window.addEventListener(event, resetTimer));

    // Initialize timer
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [session]);

  const checkMFAStatus = async () => {
    try {
      // Get current AAL level
      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (!aalError && aalData) {
        setAal(aalData.currentLevel);
      }

      // Check if MFA factors are enrolled
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (!error && data.all.length > 0) {
        const activeFactors = data.all.filter(f => f.status === "verified");
        setMfaEnabled(activeFactors.length > 0);
      } else {
        setMfaEnabled(false);
      }
    } catch (e) {
      console.error("Error checking MFA status:", e);
    }
  };

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc("is_admin", { _user_id: userId });
      if (!error) {
        setIsAdmin(data);
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    setMfaEnabled(false);
    setAal("aal1");
    toast.success("Signed out successfully");
    navigate("/");
  };

  const enrollMFA = async () => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: user?.email || "admin-totp",
    });
    return { data, error };
  };

  const verifyMFA = async (factorId: string, challengeId: string, code: string) => {
    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });
      return { error };
    } catch (e: any) {
      return { error: e };
    }
  };

  const unenrollMFA = async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({
      factorId,
    });
    if (!error) {
      checkMFAStatus();
    }
    return { error };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isAdmin, 
      isLoading, 
      mfaEnabled,
      aal,
      signIn, 
      signUp, 
      resetPassword, 
      updatePassword, 
      signOut,
      enrollMFA,
      verifyMFA,
      unenrollMFA
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
